"use client";

import { FormEvent, useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { isDeveloperEmail } from "@/lib/developer-access";
import { LOCAL_DEV_ACCESS_PROFILES } from "@/lib/local-dev-access";

export function LoginForm() {
  const router = useRouter();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLocalDevAccess, setShowLocalDevAccess] = useState(false);

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    setShowLocalDevAccess(host === "localhost" || host === "127.0.0.1");
  }, []);

  async function signInWithCredentials(email: string, password: string) {
    setError("");
    setLoading(true);

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
    });

    if (result?.error) {
      setError("Invalid credentials.");
      setLoading(false);
      return;
    }

    router.push(isDeveloperEmail(email) ? "/dev/users" : "/dashboard");
    router.refresh();
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "").trim().toLowerCase();
    const password = String(formData.get("password") ?? "");

    await signInWithCredentials(email, password);
  }

  return (
    <div className="mt-6 space-y-4">
      <form onSubmit={onSubmit} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <a href="/login/forgot-password" className="mt-2 inline-block text-sm text-orange-700 hover:text-orange-900">
              Forgot password?
            </a>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            className="w-full rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </div>
      </form>
      {showLocalDevAccess && (
        <div className="rounded-xl border border-orange-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-orange-700">Local dev access</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {LOCAL_DEV_ACCESS_PROFILES.map((profile) => (
              <a
                key={profile.email}
                href={`/api/local-dev-session?email=${encodeURIComponent(profile.email)}`}
                className="rounded-md border border-slate-200 px-3 py-2 text-center text-sm font-semibold text-slate-800 transition hover:border-orange-300 hover:bg-orange-50"
                title={profile.email}
              >
                <span className="block">{profile.label}</span>
                <span className="block text-[11px] font-normal text-slate-500">{profile.description}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
